import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { useUpdateProfile } from '@/api/hooks/useAccount';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useCreditBalance } from '@/api/hooks/useCredit';
import { useTenantBySlug } from '@/api/hooks/useTenantBySlug';
import { useVipStatus } from '@/api/hooks/useVipStatus';
import { DEFAULT_TENANT_SLUG } from '@/lib/constants';
import { formatCurrency } from '@/lib/format';
import { CalendarDays, LogOut, Menu, Settings, X, Star } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useVenueContext } from '@/providers/VenueProvider';

export default function CustomerLayout() {
  const { user, isAuthenticated, logout } = useAuth();
  const updateProfile = useUpdateProfile();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isFullWidth =
      location.pathname.startsWith('/book/')
          || /^\/venue\/[^/]+\/book\/[^/]+/.test(location.pathname);
  const { currentSlug } = useVenueContext();
  const bookHref = `/venue/${currentSlug}/book`;
  const { data: creditData } = useCreditBalance(isAuthenticated);
  const { data: tenant } = useTenantBySlug(DEFAULT_TENANT_SLUG);
  const { data: vipData } = useVipStatus(tenant?.id, isAuthenticated);
  const creditBalance = isAuthenticated ? (creditData?.balance || 0) : 0;
  const isVip = isAuthenticated && vipData?.isVip === true;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleLocale = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
    // Authenticated users get the toggle persisted to their profile so
    // a fresh login on another device picks up the same locale via the
    // AuthProvider sync effect. Fire-and-forget — the UI already shows
    // the new locale; an API failure logs but does NOT revert the page.
    // Anonymous users rely on localStorage (LanguageDetector handles
    // that path) so no profile call is needed.
    if (isAuthenticated) {
      updateProfile.mutate(
        { locale: newLang },
        {
          onError: (err) => {
            console.warn('Failed to persist language preference to profile', err);
          },
        },
      );
    }
  };

  const initials = user
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : '';

  return (
    <div className="min-h-screen bg-background">
      {/* Nav bar */}
      <header className="sticky top-0 z-50 border-b border-primary/20 bg-gradient-to-r from-background to-primary/5 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className={cn("mx-auto flex h-14 items-center justify-between px-4", !isFullWidth && "max-w-5xl")}>
          <Link to={bookHref} className="text-xl font-bold tracking-tight text-primary">
            RekPlay
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-2 md:flex">
            <Link
              to={bookHref}
              className={`${buttonVariants({ variant: 'ghost', size: 'sm' })} whitespace-nowrap`}
            >
              {t('common.book_now')}
            </Link>
            <Button variant="ghost" size="sm" className="min-w-[32px] text-center" onClick={toggleLocale}>
              {i18n.language === 'en' ? 'FR' : 'EN'}
            </Button>

            {isAuthenticated ? (
              <>
                {isVip && (
                  <span className="flex items-center gap-1 text-sm font-medium text-yellow-600">
                    <Star className="h-3.5 w-3.5 fill-yellow-400" />
                    {t('booking.vip_badge')}
                  </span>
                )}
                <Link to="/credits" className="text-sm font-medium text-primary hover:underline">
                  {creditBalance > 0
                    ? <span className="font-mono">{formatCurrency(creditBalance, 'CAD', i18n.language)} {t('credit.balance')}</span>
                    : t('credit.credits_title')}
                </Link>
                <Link
                  to="/bookings"
                  className={`${buttonVariants({ variant: 'ghost', size: 'sm' })} whitespace-nowrap`}
                >
                  <CalendarDays className="mr-1.5 h-4 w-4" />
                  {t('booking.my_bookings')}
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger className="rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate('/account')}>
                      <Settings className="mr-2 h-4 w-4" />
                      {t('account.title')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      {t('auth.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                >
                  {t('auth.login')}
                </Link>
                <Link
                  to="/register"
                  className={buttonVariants({ size: 'sm' })}
                >
                  {t('auth.register')}
                </Link>
              </>
            )}
          </nav>

          {/* Mobile menu toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="border-t px-4 pb-4 pt-2 md:hidden">
            <div className="flex flex-col gap-2">
              <Link
                to={bookHref}
                className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('common.book_now')}
              </Link>
              <Button variant="ghost" size="sm" onClick={toggleLocale}>
                {i18n.language === 'en' ? 'Français' : 'English'}
              </Button>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/bookings"
                    className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('booking.my_bookings')}
                  </Link>
                  <Button variant="ghost" size="sm" onClick={handleLogout}>
                    {t('auth.logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('auth.login')}
                  </Link>
                  <Link
                    to="/register"
                    className={buttonVariants({ size: 'sm' })}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('auth.register')}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Page content */}
      <main className={cn("px-4 py-6 animate-in fade-in duration-200", !isFullWidth && "mx-auto max-w-5xl")}>
        <Outlet />
      </main>
    </div>
  );
}
